/**
 * services/approvals/approvals.service.ts
 * ──────────────────────────────────────────────────────────────────────────
 * The approval queue and the dual-approval state machine.
 *
 * `decide` is the only write, and it enforces invariant 5 BEFORE the request
 * leaves: a rejection without a reason is rejected here, so the user gets the
 * validation toast immediately instead of a round trip that returns
 * ERR_REJECT_REASON_REQUIRED. The API enforces it too — this is the fast path,
 * not the guarantee.
 * ──────────────────────────────────────────────────────────────────────────
 */
import axiosInstance from '../../api/axiosInstance';
import {
  Cash_Approval_Decision_Url,
  Cash_Approvals_Bulk_Url,
  Cash_Approvals_Policy_Url,
  Cash_Approvals_Summary_Url,
  Cash_Approvals_Url,
} from '../../api/endpoints';
import { parseResponse } from '../../schemas/common';
import {
  approvalsResponseSchema,
  bulkResultSchema,
  decisionResultSchema,
  policyInForceSchema,
  queueSummarySchema,
  type Approval,
  type BulkResult,
  type DecisionResult,
  type PolicyInForce,
  type QueueSummary,
} from '../../schemas/approvals';

/**
 * The queue. `state: 'pending'` is what the approver's "Waiting on you" and the
 * Finance read-through both request; `'decided'` backs the Decided table.
 */
export async function fetchApprovals(
  state?: 'pending' | 'decided',
  limit?: number,
): Promise<Approval[]> {
  const { data } = await axiosInstance.get(Cash_Approvals_Url, { params: { state, limit } });
  return parseResponse(approvalsResponseSchema, data, 'GET /v1/approvals').approvals;
}

/** The approver console's three KPI cards. Value totals are per currency. */
export async function fetchQueueSummary(): Promise<QueueSummary> {
  const { data } = await axiosInstance.get(Cash_Approvals_Summary_Url);
  return parseResponse(queueSummarySchema, data, 'GET /v1/approvals/queue-summary');
}

/** The read-only quad on "Policy in force". */
export async function fetchPolicyInForce(): Promise<PolicyInForce> {
  const { data } = await axiosInstance.get(Cash_Approvals_Policy_Url);
  return parseResponse(policyInForceSchema, data, 'GET /v1/approvals/policy-in-force');
}

/**
 * Approve or reject one item.
 *
 * `released` on the result is the fact that matters, and it is not the same as
 * "approved": on a two-approver item the first approval leaves the request
 * pending and nothing moves. Callers read `released` to choose between
 * "Approved and released" and "Approved · one more approval needed".
 */
export async function decide(
  id: string,
  decision: 'approve' | 'reject',
  reason?: string,
): Promise<DecisionResult> {
  if (decision === 'reject' && !reason?.trim()) {
    // Invariant 5, checked before the request so the user sees it at once.
    throw new Error('A rejection must say why. The requester sees this reason.');
  }
  const { data } = await axiosInstance.post(Cash_Approval_Decision_Url(id), {
    decision,
    ...(reason?.trim() ? { reason: reason.trim() } : {}),
  });
  return parseResponse(decisionResultSchema, data, 'POST /v1/approvals/:id/decision');
}

/**
 * "Approve all" on the approver queue.
 *
 * The result partitions the ids: `released`, `still_pending` (dual items that
 * now hold one approval), `rejected`, and `skipped` with a reason each. Callers
 * MUST report the skipped set — a bulk action that silently drops items is how
 * a spend sits unapproved for a week while everyone believes it was cleared.
 */
export async function decideBulk(
  ids: readonly string[],
  decision: 'approve' | 'reject',
  reason?: string,
): Promise<BulkResult> {
  if (decision === 'reject' && !reason?.trim()) {
    throw new Error('A rejection must say why. The requester sees this reason.');
  }
  const { data } = await axiosInstance.post(Cash_Approvals_Bulk_Url, {
    ids,
    decision,
    ...(reason?.trim() ? { reason: reason.trim() } : {}),
  });
  return parseResponse(bulkResultSchema, data, 'POST /v1/approvals/bulk-decision');
}

export type { Approval, QueueSummary, PolicyInForce, DecisionResult, BulkResult };
